import { SUPPORT_EMAIL } from '@/lib/constants'
import { createFileRoute } from '@tanstack/react-router'
import type { LegalPageContext } from './route'

export const Route = createFileRoute('/_legal_pages/privacy')({
  component: PrivacyPolicy,
  beforeLoad: (): LegalPageContext => ({
    title: 'PRIVACY POLICY',
    lastUpdated: 'December 13, 2024',
  }),
})

function PrivacyPolicy() {
  return (
    <>
      <p className="mb-6">
        Supplyco (&quot;
        <strong>we</strong>
        ,&quot; &quot;
        <strong>us</strong>
        ,&quot; or &quot;
        <strong>our</strong>
        &quot;)
        wants you to be familiar with how we collect, use, and disclose
        information. This Privacy Policy describes our practices in
        connection with information that we collect through:
      </p>

      <ul className="list-disc pl-6 mb-6">
        <li>
          Website(s) operated by us from which you are accessing this
          Privacy Policy;
        </li>
        <li>
          Software application(s) made available by us for use on or
          through computers and mobile devices;
        </li>
        <li>
          HTML-formatted email messages that we send to you that link to
          this Privacy Policy.
        </li>
      </ul>

      <p className="mb-8">
        Collectively, we refer to the website(s), app(s), social media
        pages, and emails as the &quot;Services.&quot;
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
        Personal Information
      </h2>
      <p className="mb-4">
        &quot;
        <strong>Personal Information</strong>
        &quot; is information
        that identifies you as an individual or relates to an
        identifiable individual. We collect Personal Information through
        or in connection with the Services, such as:
      </p>

      <ul className="list-disc pl-6 mb-6">
        <li>Name</li>
        <li>Postal address</li>
        <li>Telephone number</li>
        <li>Email address</li>
        <li>
          IP address (we may also derive your approximate location from
          your IP address)
        </li>
        <li>Payment information</li>
        <li>Social media account ID</li>
      </ul>

      <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">
        Collection of Personal Information
      </h2>
      <p className="mb-4">
        We and our service providers collect Personal Information in a
        variety of ways, including:
      </p>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        Through the Services.
      </h3>
      <p className="mb-4">
        We collect Personal Information through the Services, such as
        when you sign up for a newsletter, create an account, contact
        customer service, or make a purchase.
      </p>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        From Other Sources.
      </h3>
      <p className="mb-6">
        We receive your Personal Information from other sources, such as
        publicly available databases.
      </p>
      <p className="mb-6">
        If you connect your social media account to your Services
        account, you will share certain Personal Information from your
        social media account with us, including your name, email
        address, photo, connections, and any other information that may
        be accessible to us when you connect your accounts.
      </p>

      <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">
        Use of Personal Information
      </h2>
      <p className="mb-4">
        We and our service providers use Personal Information for the
        following purposes:
      </p>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        Providing the functionality of the Services and fulfilling your
        requests.
      </h3>
      <ul className="list-disc pl-6 mb-6">
        <li>
          To provide the Services&apos; functionality to you, such as
          arranging access to your account.
        </li>
        <li>
          To complete your transactions, process cancellations, returns,
          and exchanges, and verify your information.
        </li>
        <li>
          To provide you with benefits, promotions, and customer
          service.
        </li>
        <li>To respond to your inquiries and fulfill your requests.</li>
        <li>
          To send administrative information to you, such as changes to
          our terms, conditions, and policies.
        </li>
      </ul>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        Providing you with our newsletter and/or other marketing
        materials and facilitating social sharing.
      </h3>
      <ul className="list-disc pl-6 mb-6">
        <li>To send you marketing related emails.</li>
        <li>
          To facilitate social sharing functionality that you choose to
          use.
        </li>
      </ul>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        Analyzing Personal Information for business reporting and
        providing personalized services.
      </h3>
      <ul className="list-disc pl-6 mb-6">
        <li>To analyze use of the Services.</li>
        <li>
          To better understand your interests and preferences, so that
          we can personalize our interactions with you.
        </li>
      </ul>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        Allowing you to participate in sweepstakes, contests, or other
        promotions.
      </h3>
      <p className="mb-6">
        We may offer you the opportunity to participate in a
        sweepstakes, contest, or other promotion. Some of these
        promotions have rules with information about how we will use and
        disclose your Personal Information. Please read any such rules
        before choosing to participate.
      </p>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        Aggregating and/or anonymizing Personal Information.
      </h3>
      <p className="mb-6">
        We may aggregate and/or anonymize Personal Information so that
        it will no longer be considered Personal Information. We do so
        to generate other data for our use, which we may use and
        disclose for any purpose, as it no longer identifies you or any
        other individual.
      </p>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        Accomplishing our business purposes.
      </h3>
      <ul className="list-disc pl-6 mb-6">
        <li>
          For audits, to verify that our internal processes function as
          intended and to address legal, regulatory, or contractual
          requirements;
        </li>
        <li>
          For fraud prevention and security monitoring purposes,
          including to detect and prevent cyberattacks or attempts to
          commit identity theft;
        </li>
        <li>For developing new products and services;</li>
        <li>
          For enhancing, improving, repairing, maintaining, or modifying
          our current products and services, as well as undertaking
          quality and safety assurance measures;
        </li>
        <li>
          For identifying usage trends, for example, understanding which
          parts of our Services are of most interest to users;
        </li>
        <li>
          For determining the effectiveness of our promotional
          campaigns;
        </li>
        <li>
          For operating and expanding our legitimate business
          activities.
        </li>
      </ul>

      <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">
        Disclosure of Personal Information
      </h2>
      <p className="mb-4">We disclose Personal Information:</p>
      <p className="mb-6">
        <strong>
          To our service providers, to facilitate services they provide
          to us.
        </strong>
        <br />
        These can include providers of such services as website hosting,
        data analysis, payment processing, order fulfillment, fraud
        prevention, information technology and related infrastructure
        provision, customer service, email delivery, and auditing.
      </p>

      <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">
        Other Uses and Disclosures
      </h2>
      <p className="mb-6">
        We may also use and disclose Personal Information as we believe
        to be necessary or appropriate: (a) to comply with applicable
        law, to respond to warrants pertaining to active investigations,
        and to fulfil other legal obligations; (b) to enforce our terms
        and conditions; and (c) to protect our rights, privacy, safety,
        or property, and/or that of you or others. We may use, disclose,
        or transfer Personal Information to a third party in connection
        with any proposed or actual reorganization, bankruptcy, merger,
        sale, joint venture, assignment, transfer, or other disposition
        of all or any portion of our assets or stock.
      </p>

      <h1 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
        Information Collected Automatically
      </h1>
      <p className="mb-4">
        We and our service providers may collect information
        automatically in a variety of ways, including:
      </p>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        Your browser or device.
      </h3>
      <p className="mb-6">
        Certain information is collected by most browsers or
        automatically through your device, such as your Media Access
        Control (MAC) address, computer type (Windows or Mac), screen
        resolution, operating system name and version, device
        manufacturer and model, language, and Internet browser type and
        version. We use this information to ensure that the Services
        function properly.
      </p>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        Your use of our app(s).
      </h3>
      <p className="mb-6">
        When you download and use our app, we and our service providers
        may track and collect usage data, such as the date and time the
        app on your device accesses our servers and what information and
        files have been downloaded to the app based on your device
        number. We use this information to understand how the app is
        used and improve it.
      </p>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        Cookies.
      </h3>
      <p className="mb-6">
        Cookies are pieces of information stored directly on your
        computer. Cookies allow us to collect such information as
        browser type, time spent on the Services, pages visited,
        language preferences, and traffic data. We use the information
        for security purposes, to facilitate navigation, to display
        information more effectively, and to personalize your
        experience. We also gather statistical information about use of
        the Services in order to continually improve their design and
        functionality, understand how they are used, and assist us with
        resolving questions regarding them. We do not currently respond
        to browser do-not-track signals. If you do not want information
        collected through the use of cookies, most browsers allow you to
        automatically decline cookies or be given the choice of
        declining or accepting particular cookies from a particular
        website. You may also wish to refer to
        {' '}
        <a
          href="http://www.allaboutcookies.org/manage-cookies/index.html"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#656CEC] hover:text-[#FF865B] transition-colors duration-300 underline"
        >
          http://www.allaboutcookies.org/manage-cookies/index.html
        </a>
        . If, however, you do not accept cookies, you may experience
        some inconvenience in your use of the Services.
      </p>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        Pixel tags and other similar technologies.
      </h3>
      <p className="mb-4">
        <strong>Pixel tags.</strong>
        {' '}
        We may use pixel tags (also known
        as web beacons and clear GIFs) to, among other things, track the
        actions of users of the Services (including email recipients),
        measure the success of our marketing campaigns, and compile
        statistics about use of the Services and response rates.
      </p>
      <p className="mb-4">
        <strong>Analytics.</strong>
        {' '}
        We use analytics services, such as
        Google Analytics, which use cookies and similar technologies to
        collect and analyze information about use of the Services and
        report on activities and trends. These services may also collect
        information regarding the use of other websites, apps, and
        online services. You can learn about Google&apos;s practices by
        going to
        {' '}
        <a
          href="https://www.google.com/policies/privacy/partners/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#656CEC] hover:text-[#FF865B] transition-colors duration-300 underline"
        >
          www.google.com/policies/privacy/partners/
        </a>
        {' '}
        and exercise the opt-out provided by Google by downloading the
        Google Analytics opt-out browser add-on, available at
        {' '}
        <a
          href="https://tools.google.com/dlpage/gaoptout"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#656CEC] hover:text-[#FF865B] transition-colors duration-300 underline"
        >
          https://tools.google.com/dlpage/gaoptout
        </a>
        .
      </p>
      <p className="mb-4">
        <strong>Invisible reCAPTCHA.</strong>
        {' '}
        We use Google&apos;s
        invisible reCAPTCHA application on our Services in order to
        protect against spam and other automated abuse. The reCAPTCHA
        tool may make use of cookies, as well as such other information
        as IP address, date, time, language, screen size and resolution,
        and mouse movements. The use of the reCAPTCHA tool and
        information collected through it are subject to Google&apos;s
        privacy policy, available at
        {' '}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#656CEC] hover:text-[#FF865B] transition-colors duration-300 underline"
        >
          https://policies.google.com/privacy
        </a>
        {' '}
        and Google&apos;s terms of service, available at
        {' '}
        <a
          href="https://policies.google.com/terms?hl=en"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#656CEC] hover:text-[#FF865B] transition-colors duration-300 underline"
        >
          https://policies.google.com/terms?hl=en
        </a>
        .
      </p>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        Adobe Flash technology (including Flash Local Shared Objects
        (&quot;Flash LSOs&quot;)) and similar technologies.
      </h3>
      <p className="mb-6">
        We may use Flash LSOs and other technologies to, among other
        things, collect and store information about your use of the
        Services. If you do not want Flash LSOs stored on your computer,
        you can adjust the settings of your Flash player to block Flash
        LSO storage using the tools contained in the
        {' '}
        <a
          href="https://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager07.html"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#656CEC] hover:text-[#FF865B] transition-colors duration-300 underline"
        >
          Website Storage Settings Panel
        </a>
        . You can also go to the
        {' '}
        <a
          href="https://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager03.html"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#656CEC] hover:text-[#FF865B] transition-colors duration-300 underline"
        >
          Global Storage Settings Panel
        </a>
        {' '}
        and follow the instructions. Setting the Flash Player to
        restrict or limit acceptance of Flash LSOs may reduce or impede
        the functionality of some Flash applications.
      </p>

      <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
        Physical Location.
      </h3>
      <p className="mb-6">
        We may collect the approximate physical location of your device
        by, for example, using satellite, cell phone tower, or Wi-Fi
        signals. We may use your device&apos;s physical location to
        provide you with personalized location-based services and
        content. In some instances, you may be permitted to allow or
        deny such use of your device&apos;s location, but, if you do, we
        may not be able to provide you with the applicable personalized
        services and content.
      </p>

      <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">
        Uses and Disclosures of Information Collected Automatically
      </h2>
      <p className="mb-6">
        If we are required to treat such information collected
        automatically as Personal Information under applicable law, we
        may use and disclose it for the purposes for which we use and
        disclose Personal Information, as detailed in this Privacy
        Policy. In some instances, we may combine information collected
        automatically with Personal Information. If we do, we will treat
        the combined information as Personal Information so long as it
        is combined.
      </p>

      <h1 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
        Security
      </h1>
      <p className="mb-6">
        We seek to use reasonable organizational, technical, and
        administrative measures to protect Personal Information within
        our organization. Unfortunately, no data transmission or storage
        system can be guaranteed to be 100% secure. If you have reason
        to believe that your interaction with us is no longer secure,
        please immediately notify us in accordance with the
        &quot;Contact Us&quot; section below.
      </p>

      <h1 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
        Third-Party Services
      </h1>
      <p className="mb-6">
        This Privacy Policy does not address, and we are not responsible
        for, the privacy, information, or other practices of any third
        parties, including any third party operating a website or
        service to which the Services link. The inclusion of a link on
        the Services does not imply our endorsement of the linked site
        or service.
      </p>
      <p className="mb-6">
        In addition, we are not responsible for the information
        collection, use, disclosure, or security policies or practices
        of other organizations, such as Facebook, Twitter, Instagram,
        YouTube, Pinterest, LinkedIn, Apple, Google, Microsoft, RIM, or
        any other app developer, app provider, social media platform,
        operating system provider, wireless service provider, or device
        manufacturer, including with respect to any Personal Information
        you disclose to other organizations through or in connection
        with our apps or social media pages.
      </p>

      <h1 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
        Use of the Services by Minors
      </h1>
      <p className="mb-6">
        The Services are not directed to individuals under the age of
        twenty-one (21), and we do not knowingly collect Personal
        Information from individuals under 21.
      </p>

      <h1 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
        Jurisdiction and Cross-Border Transfer
      </h1>
      <p className="mb-6">
        We are located in the United States. We may store and process
        your Personal Information in any country where we have
        facilities or in which we engage service providers. By using the
        Services, you understand that your information will be
        transferred to countries outside of your country of residence,
        including the United States, which may have data protection
        rules that are different from those of your country.
      </p>

      <h1 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
        Sensitive Information
      </h1>
      <p className="mb-6">
        Unless we request it, we ask that you not send us, and you not
        disclose, any sensitive Personal Information (e.g., Social
        Security numbers, information related to racial or ethnic
        origin, political opinions, religion or other beliefs, health,
        biometrics or genetic characteristics, or criminal background)
        on or through the Services or otherwise to us.
      </p>

      <h1 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
        Updates to this Privacy Policy
      </h1>
      <p className="mb-6">
        The &quot;Last Updated&quot; legend at the top of this Privacy
        Policy indicates when this Privacy Policy was last revised. Any
        changes will become effective when we post the revised Privacy
        Policy on the Services.
      </p>

      <h1 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
        Contact Us
      </h1>
      <p className="mb-6">
        If you have any questions about this Privacy Policy, please
        contact us at
        {' '}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="text-[#656CEC] hover:text-[#FF865B] transition-colors duration-300"
        >
          {SUPPORT_EMAIL}
        </a>
        . Because email communications are not always secure, please do
        not include credit card or other sensitive information in your
        emails to us.
      </p>
    </>
  )
}
